import React from 'react';
import { useFunnyLoadingText } from '../../hooks';
import { getNameTextColor, getAvatarGradient, getInitials } from '../../utils/nameColors';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarIcon,
  UserGroupIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

/**
 * Format a single paragraph with highlighted quotes and colored participant names.
 * Supports [[display|lookup]] annotation syntax for reliable name identification.
 * Falls back to regex matching for non-annotated text.
 */
function formatParagraphWithHighlights(text, participants = [], keyOffset = 0) {
  if (!text) return null;
  
  // Patterns for matching
  const quotePattern = /[""]([^""]+)[""]/g;
  // Match [[display|lookup]] or [[name]] annotations
  const nameAnnotationPattern = /\[\[([^\]|]+)(?:\|([^\]]+))?\]\]/g;
  
  // Fallback: case-insensitive pattern for participant names (for non-annotated text)
  const sortedNames = [...participants].sort((a, b) => b.length - a.length);
  const fallbackNamePattern = sortedNames.length > 0
    ? new RegExp(`\\b(${sortedNames.map(n => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})\\b`, 'gi')
    : null;
  
  // First pass: find all matches with their positions
  const matches = [];
  let match;
  
  // Find all name annotation matches [[display|lookup]] or [[name]]
  while ((match = nameAnnotationPattern.exec(text)) !== null) {
    const displayText = match[1];  // Text to display
    const lookupName = match[2] || match[1];  // Name for color lookup (defaults to display if no pipe)
    matches.push({
      start: match.index,
      end: match.index + match[0].length,
      type: 'annotated-name',
      displayText,
      lookupName,
    });
  }
  
  // Find all quote matches
  while ((match = quotePattern.exec(text)) !== null) {
    const matchIndex = match.index;
    const matchEnd = match.index + match[0].length;
    const matchText = match[0];
    
    // Check if this overlaps with an annotation
    const overlapsAnnotation = matches.some(
      m => matchIndex >= m.start && matchIndex < m.end
    );
    if (!overlapsAnnotation) {
      matches.push({
        start: matchIndex,
        end: matchEnd,
        type: 'quote',
        text: matchText,
      });
    }
  }
  
  // Fallback: find unannotated name matches (for older cached narratives)
  if (fallbackNamePattern) {
    while ((match = fallbackNamePattern.exec(text)) !== null) {
      const matchIndex = match.index;
      const matchEnd = match.index + match[0].length;
      const matchText = match[0];
      
      // Check if this position overlaps with any existing match
      const overlapsExisting = matches.some(
        m => (matchIndex >= m.start && matchIndex < m.end) ||
             (m.start >= matchIndex && m.start < matchEnd)
      );
      if (!overlapsExisting) {
        const originalName = participants.find(
          p => p.toLowerCase() === matchText.toLowerCase()
        ) || matchText;
        matches.push({
          start: matchIndex,
          end: matchEnd,
          type: 'fallback-name',
          displayText: matchText,
          lookupName: originalName,
        });
      }
    }
  }
  
  // Sort matches by position
  matches.sort((a, b) => a.start - b.start);
  
  // Build the result
  const parts = [];
  let lastIndex = 0;
  let keyIndex = keyOffset;
  
  for (const m of matches) {
    // Add text before this match
    if (m.start > lastIndex) {
      parts.push(text.slice(lastIndex, m.start));
    }
    
    if (m.type === 'quote') {
      parts.push(
        <span key={keyIndex++} className="font-semibold text-ink">
          {m.text}
        </span>
      );
    } else if (m.type === 'annotated-name' || m.type === 'fallback-name') {
      const colorClass = getNameTextColor(m.lookupName);
      parts.push(
        <span key={keyIndex++} className={`font-semibold ${colorClass}`}>
          {m.displayText}
        </span>
      );
    }
    
    lastIndex = m.end;
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  
  return parts.length > 0 ? parts : text;
}

/**
 * Format narrative text into paragraphs with highlighted quotes and colored participant names.
 * Each paragraph is wrapped in a <p> tag for chat bubble styling.
 * 
 * @param {string} text - The narrative text
 * @param {string[]} participants - Array of participant names to highlight (fallback)
 */
function formatNarrativeWithHighlights(text, participants = []) {
  if (!text) return null;
  
  // Split into paragraphs
  const paragraphs = text.split(/\n\n+/).filter(p => p.trim());
  
  return paragraphs.map((paragraph, idx) => (
    <p key={idx}>
      {formatParagraphWithHighlights(paragraph.trim(), participants, idx * 100)}
    </p>
  ));
}

export default function StoryViewer({ stories, currentIndex, onNavigate, allParticipants = [] }) {
  const story = stories[currentIndex];
  const canGoPrev = currentIndex > 0;
  const canGoNext = currentIndex < stories.length - 1;
  const funnyLoadingText = useFunnyLoadingText();
  
  // Handle keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft' && canGoPrev) {
        onNavigate(currentIndex - 1);
      } else if (e.key === 'ArrowRight' && canGoNext) {
        onNavigate(currentIndex + 1);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, canGoPrev, canGoNext, onNavigate]);
  
  if (!story) {
    return null;
  }
  
  const handlePrev = () => {
    if (canGoPrev) {
      onNavigate(currentIndex - 1);
    }
  };
  
  const handleNext = () => {
    if (canGoNext) {
      onNavigate(currentIndex + 1);
    }
  };
  
  return (
    <div className="card overflow-hidden p-4 sm:p-6 w-full max-w-full">
      {/* Story header */}
      <div className="border-b border-parchment pb-4 mb-6">
        <div className="hidden lg:flex items-start justify-between gap-2 mb-3">
          <div className="min-w-0">
            <h2 className="font-display text-xl sm:text-2xl font-bold text-ink mb-1 truncate">
              {story.title_emoji && <span className="mr-2">{story.title_emoji}</span>}
              {story.title}
            </h2>
            <p className="text-sm text-sepia">
              Chapter {story.chapter_index + 1}
            </p>
          </div>
          
          {/* Navigation */}
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <button
              onClick={handlePrev}
              disabled={!canGoPrev}
              className={`
                p-1.5 sm:p-2 rounded-lg transition-colors
                ${canGoPrev 
                  ? 'hover:bg-parchment/50 text-sepia' 
                  : 'text-sepia/30 cursor-not-allowed'
                }
              `}
            >
              <ChevronLeftIcon className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
            
            <span className="text-xs sm:text-sm text-sepia whitespace-nowrap">
              {currentIndex + 1} / {stories.length}
            </span>
            
            <button
              onClick={handleNext}
              disabled={!canGoNext}
              className={`
                p-1.5 sm:p-2 rounded-lg transition-colors
                ${canGoNext 
                  ? 'hover:bg-parchment/50 text-sepia' 
                  : 'text-sepia/30 cursor-not-allowed'
                }
              `}
            >
              <ChevronRightIcon className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>
        
        {/* Meta info */}
        <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm text-sepia">
          {story.date && (
            <div className="flex items-center gap-1.5">
              <CalendarIcon className="w-4 h-4" />
              <span>{story.date}</span>
            </div>
          )}
          
          {story.participants && story.participants.length > 0 && (
            <div className="flex items-center gap-1.5">
              <UserGroupIcon className="w-4 h-4" />
              <span className="flex flex-wrap items-center gap-1">
                {story.participants.map((name, idx) => (
                  <span key={idx} className="inline-flex items-center">
                    <span 
                      className={`w-4 h-4 rounded-full bg-gradient-to-br ${getAvatarGradient(name)} inline-flex items-center justify-center text-white text-[7px] font-bold mr-1`}
                    >
                      {getInitials(name)}
                    </span>
                    <span className={`font-medium ${getNameTextColor(name)}`}>{name}</span>
                    {idx < story.participants.length - 1 && <span className="text-sepia/40 mx-1">·</span>}
                  </span>
                ))}
              </span>
            </div>
          )}
          
          {story.time_range && (
            <div className="flex items-center gap-1.5">
              <ClockIcon className="w-4 h-4" />
              <span>{story.time_range}</span>
            </div>
          )}
        </div>
        
        {/* Expression tags */}
        {story.tags && story.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {story.tags.map((tag, tagIdx) => (
              <span 
                key={tagIdx}
                className="px-3 py-1 text-xs rounded-full bg-gold/20 text-rust font-medium"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
      
      {/* Story content */}
      <div className="prose prose-sepia max-w-full overflow-hidden">
        <div className="font-body text-ink leading-relaxed chat-bubbles break-words w-full">
          {story.narrative ? formatNarrativeWithHighlights(story.narrative, allParticipants) : 'No story content available.'}
        </div>
      </div>
      
      {/* Story illustration - after text, smaller */}
      {(story.illustration_url || story.illustration_loading) && (
        <div className="mt-8 flex justify-center">
          <div className="relative max-w-xs w-full">
            {story.illustration_loading && !story.illustration_url ? (
              // Loading placeholder
              <div className="w-full aspect-square rounded-lg border border-parchment/50 bg-gradient-to-br from-parchment/30 to-cream flex flex-col items-center justify-center">
                <div className="w-10 h-10 mb-3 relative">
                  <div className="absolute inset-0 rounded-full border-2 border-parchment"></div>
                  <div className="absolute inset-0 rounded-full border-2 border-rust border-t-transparent animate-spin"></div>
                </div>
                <p className="text-sm text-sepia/70 font-medium">Creating illustration...</p>
                <p className="text-xs text-sepia/50 mt-1 italic px-4 text-center">{funnyLoadingText}</p>
              </div>
            ) : story.illustration_url ? (
              // Actual illustration
              <>
                <img
                  src={story.illustration_url}
                  alt={`Illustration for ${story.title}`}
                  className="w-full h-auto rounded-lg shadow-sm border border-parchment/50"
                  loading="lazy"
                />
                <p className="text-xs text-sepia/50 text-center mt-2 italic">
                  AI-generated illustration
                </p>
              </>
            ) : null}
          </div>
        </div>
      )}
      
      {/* Story footer */}
      <div className="mt-8 pt-4 border-t border-parchment">
        <div className="flex items-center justify-end gap-4">
          {/* Quick navigation dots - limited to max 15 visible with smart windowing */}
          <div className="flex gap-1.5 items-center overflow-hidden">
            {(() => {
              const maxDots = 15;
              const total = stories.length;
              
              if (total <= maxDots) {
                // Show all dots if within limit
                return stories.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => onNavigate(idx)}
                    className={`
                      w-2 h-2 rounded-full transition-all duration-200 shrink-0
                      ${idx === currentIndex 
                        ? 'bg-rust w-4' 
                        : 'bg-parchment hover:bg-sepia/30'
                      }
                    `}
                  />
                ));
              }
              
              // Smart windowing: show dots around current position
              const halfWindow = Math.floor((maxDots - 3) / 2); // -3 for ellipsis indicators
              let start = Math.max(0, currentIndex - halfWindow);
              let end = Math.min(total - 1, currentIndex + halfWindow);
              
              // Adjust if at edges
              if (start === 0) {
                end = Math.min(total - 1, maxDots - 2);
              }
              if (end === total - 1) {
                start = Math.max(0, total - maxDots + 1);
              }
              
              const dots = [];
              
              // Leading ellipsis
              if (start > 0) {
                dots.push(
                  <button
                    key="start"
                    onClick={() => onNavigate(0)}
                    className="w-2 h-2 rounded-full bg-parchment hover:bg-sepia/30 shrink-0"
                  />,
                  <span key="start-ellipsis" className="text-sepia/40 text-xs px-0.5">…</span>
                );
              }
              
              // Visible dots
              for (let idx = start; idx <= end; idx++) {
                dots.push(
                  <button
                    key={idx}
                    onClick={() => onNavigate(idx)}
                    className={`
                      w-2 h-2 rounded-full transition-all duration-200 shrink-0
                      ${idx === currentIndex 
                        ? 'bg-rust w-4' 
                        : 'bg-parchment hover:bg-sepia/30'
                      }
                    `}
                  />
                );
              }
              
              // Trailing ellipsis
              if (end < total - 1) {
                dots.push(
                  <span key="end-ellipsis" className="text-sepia/40 text-xs px-0.5">…</span>,
                  <button
                    key="end"
                    onClick={() => onNavigate(total - 1)}
                    className="w-2 h-2 rounded-full bg-parchment hover:bg-sepia/30 shrink-0"
                  />
                );
              }
              
              return dots;
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}

